import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CONFIG } from '@/lib/config';

// Add custom styles for animations
const styles = `
  @keyframes slide-in {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  .animate-slide-in {
    animation: slide-in 0.3s ease-out;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
import { 
  Mail, 
  Send, 
  FileText, 
  Users, 
  Building2, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  Trash2,
  Edit,
  Eye,
  Copy,
  Search,
  Filter,
  EyeIcon,
  TestTube
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Email Template Interface
interface EmailTemplate {
  id: string;
  key: string;
  name: string;
  kind: 'notification' | 'marketing';
  from: string;
  replyTo?: string;
  subject: string;
  html: string;
  text?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Email Log Interface
interface EmailLog {
  id: string;
  templateId?: string;
  to: string;
  subject: string;
  status: 'queued' | 'sent' | 'failed';
  providerMessageId?: string;
  error?: string;
  sentAt?: string;
  createdAt: string;
  templateKey?: string;
  hubName?: string;
  projectName?: string;
  sentFrom?: string;
}

// Send Email Request Interface
interface SendEmailRequest {
  to: string;
  from?: string;
  replyTo?: string;
  templateKey?: string;
  variables?: Record<string, any>;
  subject?: string;
  html?: string;
  text?: string;
}

const AdminNotifications: React.FC = () => {
  // State for templates
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState<Partial<EmailTemplate>>({
    key: '',
    name: '',
    kind: 'notification',
    from: 'noreply@example.com',
    subject: '',
    html: '',
    text: '',
    isActive: true
  });

  // State for email logs
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [logsPagination, setLogsPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  const [logsFilters, setLogsFilters] = useState({
    status: '',
    templateKey: '',
    from: '',
    to: ''
  });

  // State for send test
  const [sendTestData, setSendTestData] = useState<SendEmailRequest>({
    to: '',
    from: 'noreply@example.com',
    templateKey: '',
    variables: {},
    subject: '',
    html: ''
  });
  const [sendTestMode, setSendTestMode] = useState<'template' | 'raw'>('template');
  const [isSending, setIsSending] = useState(false);

  // API Base URL
  const API_BASE_URL = CONFIG.API_BASE_URL;

  // Load templates
  const loadTemplates = async () => {
    try {
      console.log('🔄 Loading templates...');
      const response = await fetch(`${API_BASE_URL}/api/email/templates?t=${Date.now()}`, {
        credentials: 'include',
        cache: 'no-cache'
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Templates loaded:', data);
        setTemplates(data);
      } else {
        console.error('❌ Failed to load templates:', response.status);
      }
    } catch (error) {
      console.error('❌ Error loading templates:', error);
    }
  };

  // Load email logs
  const loadEmailLogs = async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: logsPagination.pageSize.toString(),
        ...logsFilters
      });
      
      const response = await fetch(`${API_BASE_URL}/api/email/logs?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Convert API response fields from snake_case to camelCase
        const convertedLogs = data.logs.map((log: any) => ({
          ...log,
          sentAt: log.sent_at,
          hubName: log.hub_name,
          projectName: log.project_name,
          sentFrom: log.sent_from,
          templateKey: log.template_key || null
        }));
        setEmailLogs(convertedLogs);
        setLogsPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error loading email logs:', error);
    }
  };

  // Test email functionality
  const sendTestEmail = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to: testEmail,
          from: newTemplate.from,
          subject: newTemplate.subject,
          html: newTemplate.html,
          text: newTemplate.text
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Test email sent successfully! ${result.message || ''}`);
        setShowTestModal(false);
        setTestEmail('');
      } else {
        const errorData = await response.json();
        alert(`Failed to send test email: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      alert(`Error sending test email: ${error.message || 'Network error'}`);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadEmailLogs();
  }, []);

  // Template management functions
  const saveTemplate = async () => {
    try {
      const url = editingTemplate 
        ? `${API_BASE_URL}/api/email/templates/${editingTemplate.id}`
        : `${API_BASE_URL}/api/email/templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newTemplate,
          is_active: newTemplate.isActive // Fix: map isActive to is_active for API
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Template saved successfully:', result);
        
        // Force reload templates with a small delay to ensure database consistency
        setTimeout(() => {
          console.log('🔄 Force reloading templates after update...');
          loadTemplates();
        }, 100);
        
        setShowTemplateModal(false);
        setEditingTemplate(null);
        setNewTemplate({
          key: '',
          name: '',
          kind: 'notification',
          from: 'noreply@example.com',
          subject: '',
          html: '',
          text: '',
          isActive: true
        });
        // Show success message with elegant UI
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      } else {
        const errorData = await response.json();
        console.error('❌ Error saving template:', errorData);
        alert(`Error saving template: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error saving template:', error);
      alert(`Error saving template: ${error.message || 'Network error'}`);
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/email/templates/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        loadTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };


  // Utility functions
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'queued':
        return <Badge className="bg-blue-100 text-blue-800"><Clock className="w-3 h-3 mr-1" />Queued</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-slide-in">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="font-medium">Template saved successfully!</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Management</h1>
          <p className="text-gray-600">Manage email templates, send test emails, and view logs</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Templates</p>
                <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sent</p>
                <p className="text-2xl font-bold text-gray-900">
                  {emailLogs.filter(log => log.status === 'sent').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {emailLogs.filter(log => log.status === 'failed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Mail className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Logs</p>
                <p className="text-2xl font-bold text-gray-900">{logsPagination.totalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="templates" className="space-y-6">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
        <Card>
          <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Email Templates</CardTitle>
                <Button onClick={() => setShowTemplateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
              </Button>
            </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Key</TableHead>
                      <TableHead>Kind</TableHead>
                      <TableHead>From</TableHead>
                  <TableHead>Status</TableHead>
                      <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                    {templates.map((template) => (
                      <TableRow key={template.id}>
                    <TableCell>
                      <div>
                            <div className="font-medium">{template.name}</div>
                            <div className="text-sm text-gray-500">{template.subject}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">{template.key}</code>
                    </TableCell>
                    <TableCell>
                          <Badge variant={template.kind === 'notification' ? 'default' : 'secondary'}>
                            {template.kind}
                          </Badge>
                    </TableCell>
                        <TableCell>{template.from}</TableCell>
                    <TableCell>
                          <Badge variant={template.is_active ? 'default' : 'secondary'}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                    </TableCell>
                        <TableCell>{formatDate(template.updated_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingTemplate(template);
                                setNewTemplate({
                                  ...template,
                                  isActive: template.is_active // Fix: use is_active from API
                                });
                                setShowTemplateModal(true);
                              }}
                            >
                          <Edit className="w-4 h-4" />
                        </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => deleteTemplate(template.id)}
                            >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>


        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Email Logs</CardTitle>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Filter by status..."
                    value={logsFilters.status}
                    onChange={(e) => setLogsFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="w-40"
                  />
                  <Button onClick={() => loadEmailLogs()}>
                    <Search className="w-4 h-4 mr-2" />
                    Filter
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>To</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Sent At</TableHead>
                      <TableHead>Hub Name</TableHead>
                      <TableHead>Project Name</TableHead>
                      <TableHead>Sent From</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emailLogs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.to}</TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate">{log.subject}</div>
                        </TableCell>
                        <TableCell>
                          {log.templateKey ? (
                            <code className="text-sm bg-gray-100 px-2 py-1 rounded">{log.templateKey}</code>
                          ) : (
                            <span className="text-gray-500">Raw</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell>
                          {log.sentAt ? formatDate(log.sentAt) : '-'}
                        </TableCell>
                        <TableCell>{log.hubName || '-'}</TableCell>
                        <TableCell>{log.projectName || '-'}</TableCell>
                        <TableCell>{log.sentFrom || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Showing {emailLogs.length} of {logsPagination.totalCount} logs
                </div>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => loadEmailLogs(logsPagination.page - 1)}
                    disabled={logsPagination.page <= 1}
                  >
                    Previous
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => loadEmailLogs(logsPagination.page + 1)}
                    disabled={logsPagination.page >= logsPagination.totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-[90vw] h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>
                {editingTemplate ? 'Edit Template' : 'New Template'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Template Key</label>
                  <Input
                    value={newTemplate.key}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, key: e.target.value }))}
                    placeholder="quotation_ready"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Quotation Ready Email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Kind</label>
                  <Select 
                    value={newTemplate.kind} 
                    onValueChange={(value: any) => setNewTemplate(prev => ({ ...prev, kind: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notification">Notification</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">From Email</label>
                  <Input
                    value={newTemplate.from}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, from: e.target.value }))}
                    placeholder="noreply@example.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Reply To</label>
                  <Input
                    value={newTemplate.replyTo || ''}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, replyTo: e.target.value }))}
                    placeholder="support@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <Input
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Your quotation {{quoteId}} is ready"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">HTML Content</label>
                <Textarea
                  value={newTemplate.html}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, html: e.target.value }))}
                  placeholder="<h1>Hello {{customerName}}</h1><p>Your quotation {{quoteId}} is ready...</p>"
                  rows={8}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">HTML Preview</label>
                  <div className="flex space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPreviewModal(true)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Show Preview
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowTestModal(true)}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Send Test
                    </Button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500 italic">
                  Click "Show Preview" to see how your email will look in a large, scalable window
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={newTemplate.isActive}
                  onChange={(e) => setNewTemplate(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Active Template
                </label>
              </div>

              <div className="flex space-x-2">
                <Button onClick={saveTemplate}>
                  {editingTemplate ? 'Update Template' : 'Create Template'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                    setNewTemplate({
                      key: '',
                      name: '',
                      kind: 'notification',
                      from: 'noreply@example.com',
                      subject: '',
                      html: '',
                      text: '',
                      isActive: true
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* HTML Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="w-[95vw] h-[95vh] bg-white rounded-lg shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">HTML Preview</h3>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const iframe = document.getElementById('preview-iframe') as HTMLIFrameElement;
                    if (iframe) {
                      iframe.style.transform = 'scale(0.5)';
                    }
                  }}
                >
                  Zoom Out
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const iframe = document.getElementById('preview-iframe') as HTMLIFrameElement;
                    if (iframe) {
                      iframe.style.transform = 'scale(1)';
                    }
                  }}
                >
                  Normal
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const iframe = document.getElementById('preview-iframe') as HTMLIFrameElement;
                    if (iframe) {
                      iframe.style.transform = 'scale(1.5)';
                    }
                  }}
                >
                  Zoom In
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreviewModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                id="preview-iframe"
                className="w-full h-full border-0"
                style={{ 
                  transform: 'scale(1)',
                  transformOrigin: 'top left',
                  width: '100%',
                  height: '100%'
                }}
                srcDoc={newTemplate.html || '<p>No HTML content</p>'}
                title="HTML Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Email Address
                </label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                />
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>Subject:</strong> {newTemplate.subject}</p>
                <p><strong>From:</strong> {newTemplate.from}</p>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTestModal(false);
                  setTestEmail('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={sendTestEmail}>
                Send Test Email
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
